import {
  IS_DENTAL_AND_VISION,
  IS_SHORT_TERM,
  MULTI_CARRIER_ENABLED
} from 'core/constants/environment'
import {
  PPI_330_ADDITIONAL_PRODUCTS_TAB,
  PPI_330_ADDITIONAL_PRODUCTS_TAB_VARIANTS
} from 'core/constants/experiments'
import { DENTAL_STAND_ALONE } from 'core/constants/products'
import { getVariant } from 'core/experiment'
import isEmpty from 'utils/object/isEmpty'
import getPagePath from './getPagePath'
import store from 'utils/store/withMiddleware'

export interface Product {
  name: string
  link: string
  text: string
  isActive: boolean
  hasCoverage?: boolean
  tag?: string
}

/**
 * Responds with a boolean value indicating if there is coverage or not, returns `undefined` if an invalid coverage response was provided.
 * @param coverageResponse The coverage response
 * @param key The product key, undefined will return the `data.coverage` property
 */
function getHasCoverage(coverageResponse: any, key?: number): boolean | undefined {
  if (coverageResponse === undefined) return undefined
  const { error, data } = coverageResponse
  if (error) return undefined

  if (key === undefined) {
    return !isEmpty(data.coverage)
  }
  return data.countByProduct[key] !== undefined
}

const createAll = ({ pagePath, coverageResponse, maxDuration, selectedTab }) => {
  const dentalVisionProduct: Product = {
    name: 'dental',
    link: `/product/dental-and-vision${pagePath}`,
    text: 'Dental and Vision',
    isActive: selectedTab === 'dental-and-vision',
    hasCoverage: getHasCoverage(coverageResponse, DENTAL_STAND_ALONE)
  }

  const defaultSTMProduct: Product = {
    name: 'stm',
    link: `/product/short-term-health-insurance${pagePath}`,
    text: 'Short Term',
    isActive: selectedTab === 'stm',
    hasCoverage: getHasCoverage(coverageResponse)
  }

  const stm4MonthProduct: Product = {
    name: 'stm-4',
    link: `/product/short-term-health-insurance${pagePath}?months=4&maxDuration=${maxDuration}`,
    text: 'Short Term - Up to 4 Months',
    isActive: selectedTab === 'stm-4',
    hasCoverage: getHasCoverage(coverageResponse)
  }

  const stmLargerMonthProduct: Product = {
    name: 'stm-multi',
    link: `/product/short-term-health-insurance${pagePath}?months=${maxDuration}&maxDuration=${maxDuration}`,
    text: `Short Term - Up to ${maxDuration} Months`,
    isActive: selectedTab === 'stm-multi',
    hasCoverage: getHasCoverage(coverageResponse)
  }

  const additionalProducts: Product = {
    name: 'aditional-products',
    link: `/product/short-term-health-insurance${pagePath}?tab=additional-products`,
    text: `Additional Insurance Products`,
    isActive: selectedTab === 'additional-products',
    hasCoverage: getHasCoverage(coverageResponse)
  }

  return {
    dentalVisionProduct,
    defaultSTMProduct,
    stm4MonthProduct,
    stmLargerMonthProduct,
    additionalProducts
  }
}

export function getProductsWithCoverageFlag(coverageResponse): Product[] {
  // Get all the values from the context including experiment values
  const state = store.getState()
  const pagePath = getPagePath()
  const searchParams = new URLSearchParams(window.location.search)
  const maxDuration = searchParams.get('maxDuration') || state.eligibilityMonths
  const months = searchParams.get('months')
  const tabValue = searchParams.get('tab')
  const enableMultiCarrier = MULTI_CARRIER_ENABLED && state.eligibilityBool

  const enableAdditionalProducts =
    getVariant({
      experimentKey: PPI_330_ADDITIONAL_PRODUCTS_TAB,
      fallback: PPI_330_ADDITIONAL_PRODUCTS_TAB_VARIANTS.CONTROL
    }) === PPI_330_ADDITIONAL_PRODUCTS_TAB_VARIANTS.ADDITIONAL_PRODUCTS_TAB

  // Identify the selected product
  let selectedTab = ''
  if (tabValue === 'additional-products') {
    selectedTab = 'additional-products'
  } else if (IS_DENTAL_AND_VISION) {
    selectedTab = 'dental-and-vision'
  } else if (MULTI_CARRIER_ENABLED && +months > 4) {
    selectedTab = 'stm-multi'
  } else if (MULTI_CARRIER_ENABLED && +months === 4) {
    selectedTab = 'stm-4'
  } else if (IS_SHORT_TERM) {
    selectedTab = 'stm'
  }

  // Create the product menu items
  const {
    defaultSTMProduct,
    dentalVisionProduct,
    stm4MonthProduct,
    stmLargerMonthProduct,
    additionalProducts
  } = createAll({
    pagePath,
    coverageResponse,
    maxDuration,
    selectedTab
  })

  // Return the products for census page
  if (pagePath.includes('census')) {
    return [defaultSTMProduct, dentalVisionProduct]
  }

  // Return the products for quotes page
  if (pagePath.includes('quotes')) {
    const products = []

    if (enableMultiCarrier) {
      products.push(stm4MonthProduct)
      products.push(stmLargerMonthProduct)
    } else {
      products.push(defaultSTMProduct)
    }

    products.push(dentalVisionProduct)

    if (enableAdditionalProducts) {
      products.push(additionalProducts)
    }

    return products
  }

  return []
}

export default getProductsWithCoverageFlag
